import Vue from "vue";
import { apolloClient } from "../../../vue-apollo";

import {
  LOGIN_WITH_ADM,
  CHECK_TOKEN,
  CHECK_TOKEN_RECOVERY,
  RECOVER_PASS,
  USUARIO_LOGIN_TOKEN,
  EDIT_PASSWORD_RECUPERACION,
} from "../../../apollo/mutations";

const USER_LS_KEY = "vuejs__user";

// El usuario vive en localStorage (vue-ls) para sobrevivir a las recargas.
// Se lee crudo porque este módulo se importa antes de que se instale el plugin,
// así que Vue.prototype.$ls todavía no existe al inicializar el state.
function getUserFromStorage() {
  try {
    const raw = localStorage.getItem(USER_LS_KEY);
    return (raw && JSON.parse(raw)?.value) || {};
  } catch {
    return {};
  }
}

export default {
  namespaced: true,
  state: {
    user_data: getUserFromStorage(),
  },

  // Identidad del usuario. Es la única fuente para id_empresa / id_usuario y demás
  // datos de sesión: los componentes deben usar estos getters en vez de leer
  // this.$ls.get("user") directamente, para que los payloads sean reactivos y
  // dejen de depender del formato con que vue-ls guarda el objeto.
  getters: {
    usuario: (state) => state.user_data || {},
    // El back manda la empresa por duplicado: como id_empresa_fk y dentro de
    // empresa[0]. En el admin lo habitual era leer empresa[0], así que se usa
    // como respaldo por si algún checkToken viejo no trae id_empresa_fk.
    idEmpresa: (_, getters) =>
      getters.usuario.id_empresa_fk ?? getters.empresa?.id_empresa,
    idUsuario: (_, getters) => getters.usuario.id_usuario,
    // El back devuelve privilegio y empresa como arreglos de un elemento.
    privilegio: (_, getters) => getters.usuario.privilegio?.[0],
    idPrivilegio: (_, getters) => getters.privilegio?.id_tipo,
    empresa: (_, getters) => getters.usuario.empresa?.[0],
    grupo: (_, getters) => {
      const g = getters.usuario.grupo;
      return Array.isArray(g) && typeof g[0] === "object" && g[0] != null ? g[0] : null;
    },
    // Ojo: el admin usa la cadena "null" para decir "sin grupo", no undefined.
    // Es lo que ya hacían Login.vue y App.vue, y lo que espera getModules.
    idGrupo: (_, getters) => getters.grupo?.id_grupo ?? "null",
    colorPrimario: (_, getters) =>
      getters.empresa?.color_primario ? `#${getters.empresa.color_primario}` : "",
    estaLogueado: (_, getters) => !!getters.idUsuario,
    // Atajo para los payloads que piden ambos ids, que son la mayoría:
    // { ...identidad, otra_cosa }
    identidad: (_, getters) => ({
      id_empresa: getters.idEmpresa,
      id_usuario: getters.idUsuario,
    }),
  },

  mutations: {
    // Datos del usuario obtenidos al loguearse o con checkToken: quedan en el
    // store (reactivo, para los componentes) y en localStorage (persistencia
    // entre recargas). Se guarda primero y se relee para que el state sea una
    // copia propia y no una referencia al objeto de la caché de Apollo, y para
    // que store y localStorage contengan exactamente lo mismo.
    SET_USER_DATA(state, data_) {
      Vue.prototype.$ls.set("user", data_);
      state.user_data = getUserFromStorage();
    },

    // Vuelve a leer localStorage. Necesario porque hay pantallas que escriben
    // ahí directamente al editar el perfil (imagen, teléfonos, correo).
    REFRESH_USER_DATA(state) {
      state.user_data = getUserFromStorage();
    },

    CLEAR_USER_DATA(state) {
      Vue.prototype.$ls.remove("user");
      state.user_data = {};
    },
  },
  actions: {
    async getUserDataFromTokenAction(_, data_) {
      const params = {
        mutation: CHECK_TOKEN,
        variables: data_,
      };

      let response = null;
      try {
        const res = await apolloClient.mutate(params);

        if (!res.loading) response = res.data.checkToken;
      } catch (e) {
        console.error("Error en chequeo de token:", e);
      }
      return response;
    },

    async loginAdmin(_, data_) {
      const params = {
        mutation: LOGIN_WITH_ADM,
        variables: data_,
      };

      let response = null;
      try {
        const res = await apolloClient.mutate(params);
        if (!res.loading) response = res.data.usuarioLoginTokenAdmin;
      } catch (e) {
        console.error("Error iniciando sesión en Administrador:", e);
      }
      return response;
    },

    async recoverPasswordAction(_, data_) {
      const params = {
        mutation: RECOVER_PASS,
        variables: data_,
      };

      let response = null;
      try {
        const res = await apolloClient.mutate(params);
        if (!res.loading) response = res.data.recuperarPassword;
      } catch (e) {
        console.error("Error recuperando contraseña:", e);
      }
      return response;
    },

    async checkTokenRecoveryAction(_, data_) {
      const params = {
        mutation: CHECK_TOKEN_RECOVERY,
        variables: data_,
      };

      let response = null;
      try {
        const res = await apolloClient.mutate(params);
        // devolvemos false para los casos cuando checkTokenRecuperar sea null,
        // para desambiguar la respuesta de esta función cuando haya error en la
        // llamada del WS (que ocasiona retornar response = null, ver el catch ↓)
        if (!res.loading) response = res.data.checkTokenRecuperar ?? false;
      } catch (e) {
        console.error("Error obteniendo id desde token:", e);
      }
      return response;
    },
    async editPasswordRecuperacion(_, data_) {
      const params = {
        mutation: EDIT_PASSWORD_RECUPERACION,
        variables: data_, // id_usuario, password, token, uuid
      };
      let response = null;
      try {
        const res = await apolloClient.mutate(params);
        if (!res.loading) response = res.data.editPasswordRecuperacion ?? false;
      } catch (e) {
        console.error("Error obteniendo id desde token:", e);
      }
      return response;
    },

    async getAuthTokens(_, payload) {
      const params = {
        mutation: USUARIO_LOGIN_TOKEN,
        variables: payload,
      };
      let response = null;
      try {
        const res = await apolloClient.mutate(params);
        if (!res.loading) response = res.data.usuarioLoginToken;
      } catch (e) {
        console.error("Error getAuthTokens:", e);
      }
      return response;
    },
  },
};
